import {
    DisplayOption,
    FullLayoutItem,
    LayoutList,
    LayoutPackageDetails,
    MultiRelationsMap,
    NewSubLayout,
    SingleRelationsMap,
} from '@/types/pages/layout';
import { defaultState, Dialogs, LAYOUT_TYPE, LayoutDefault, Loadings, PackageInfo } from './defaultState';
import {
    buildLayoutsById,
    detachIds,
    detachSingleIds,
    getSingleIds,
    getStyleIds,
    getStyleInfo,
    mergeListAndConfig,
    mergeListAndConfigForSingle,
    removeRelationsById,
    removeSingleRelationsById,
    setSingleStyleIds,
    setStyleIds,
} from '@/utils/layouts/layoutUtils';
import { isEmpty } from 'lodash';

export type LayoutAction =
    | { type: 'SET_PACKAGE_NAME'; payload: string }
    | { type: 'SET_PACKAGE_INFO'; payload: PackageInfo }
    | { type: 'SET_PREVIEW_BARCODE_DETAIL'; payload: string }
    | { type: 'SET_SAVE_AS_NAME'; payload: string }
    | { type: 'SET_CURRENT_TAB'; payload: LAYOUT_TYPE }
    | { type: 'SET_DISPLAY_OPTIONS'; payload: DisplayOption[] }
    | { type: 'SET_LAYOUT_INFO'; payload: LayoutPackageDetails }
    | { type: 'CLEAR_EDIT_DATA' }
    | { type: 'TOGGLE_SELECTED_LAYOUT'; payload: { id: string; checked: boolean } }
    | { type: 'UPDATE_EDIT_DATA'; payload: FullLayoutItem }
    | {
          type: 'STORE_EDIT_DATA';
          payload: {
              id: string;
              colors: string[];
              bpp: number | null;
              resolution: string;
              updated: {
                  name: string;
                  purpose: number;
                  content: string;
              };
          };
      }
    | {
          type: 'SET_PREVIEW_ERROR';
          payload: {
              key: string;
              message: string;
          }[];
      }
    | {
          type: 'TOGGLE_LOADING';
          payload: {
              key: keyof Loadings;
              loading: boolean;
          };
      }
    | {
          type: 'TOGGLE_DIALOG';
          payload: {
              key: keyof Dialogs;
              open: boolean;
          };
      }
    | { type: 'INIT_MULTI'; payload: { layouts: FullLayoutItem[]; relations: MultiRelationsMap } | null }
    | { type: 'REORDER_MULTI'; newList: LayoutList[] }
    | { type: 'DETACH_MULTI'; ids: string[] }
    | { type: 'SET_MULTI_DEFAULT'; payload: string }
    | { type: 'DELETE_SUB_LAYOUT_PACKAGE' }
    | { type: 'DELETE_SUB_LAYOUT'; payload: string }
    | { type: 'SET_MULTI_DISPLAY'; payload: string }
    | { type: 'ADD_NEW_SUB_LAYOUT'; payload: NewSubLayout }
    | { type: 'SET_MULTI_RELATIONS'; payload: { displayType: string; selectedList: LayoutList[]; styleSlug: string } }
    | { type: 'SET_STYLE_ID'; styleId: number | null }
    | { type: 'SET_SELECTED_COLORS'; payload: string[] }
    | { type: 'SET_SEARCH_SUB'; payload: string }
    | { type: 'CLEAN_SELECTED_LAYOUT' }
    | { type: 'INIT_SINGLE'; payload: { layouts: FullLayoutItem[]; relations: SingleRelationsMap } | null }
    | { type: 'SET_SINGLE_DISPLAY'; payload: string }
    | { type: 'REORDER_SINGLE'; newList: LayoutList[] }
    | { type: 'DETACH_SINGLE'; ids: string[] }
    | { type: 'SET_SINGLE_RELATIONS'; payload: { displayType: string; selectedList: LayoutList[] } }
    | { type: 'CLEAN_SINGLE_SELECTED_LAYOUT' }
    | { type: 'DELETE_SINGLE_LAYOUT_PACKAGE' }
    | { type: 'SET_SINGLE_SELECTED_SIZE'; payload: string[] }
    | { type: 'SET_SINGLE_SELECTED_COLORS'; payload: string[] }
    | { type: 'SET_SINGLE_SEARCH_SUB'; payload: string }
    | { type: 'TOGGLE_SINGLE_SELECTED_LAYOUT'; payload: { id: string; checked: boolean } }
    | { type: 'UPDATE_SINGLE_EDIT_DATA'; payload: FullLayoutItem }
    | {
          type: 'STORE_SINGLE_EDIT_DATA';
          payload: {
              id: string;
              colors: string[];
              size: string;
              bpp: number | null;
              updated: {
                  name: string;
                  purpose: number;
                  content: string;
              };
          };
      }
    | { type: 'CLEAR_SINGLE_EDIT_DATA' }
    | { type: 'DELETE_SINGLE_LAYOUT'; payload: string }
    | { type: 'ADD_NEW_SINGLE_LAYOUT'; payload: NewSubLayout }
    | { type: 'SET_SINGLE_DEFAULT'; payload: string };

export const reducer = (state: LayoutDefault, action: LayoutAction): LayoutDefault => {
    switch (action.type) {
        case 'SET_PACKAGE_NAME': {
            return {
                ...state,
                packageName: action.payload,
            };
        }
        case 'SET_PACKAGE_INFO': {
            return {
                ...state,
                packageInfo: action.payload,
            };
        }
        case 'SET_PREVIEW_BARCODE_DETAIL': {
            return {
                ...state,
                previewBarcode: action.payload,
            };
        }
        case 'SET_CURRENT_TAB': {
            return {
                ...state,
                currentTab: action.payload,
            };
        }
        case 'SET_DISPLAY_OPTIONS': {
            return {
                ...state,
                displayOptions: action.payload,
            };
        }
        case 'SET_LAYOUT_INFO': {
            return {
                ...state,
                layoutInfo: action.payload,
            };
        }
        case 'TOGGLE_SELECTED_LAYOUT': {
            const { id, checked } = action.payload;
            const { selectedLayouts } = state.multi;
            return {
                ...state,
                multi: {
                    ...state.multi,
                    selectedLayouts: checked ? [...selectedLayouts, id] : selectedLayouts.filter((o) => o !== id),
                },
            };
        }
        case 'UPDATE_EDIT_DATA': {
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    edit: true,
                },
                multi: {
                    ...state.multi,
                    editLayout: action.payload,
                },
            };
        }
        case 'STORE_EDIT_DATA': {
            const { id, colors, updated, bpp, resolution } = action.payload;
            const { layoutsById } = state.multi;
            return {
                ...state,
                multi: {
                    ...state.multi,
                    layoutsById: {
                        ...layoutsById,
                        [id]: {
                            ...layoutsById[id],
                            name: updated.name,
                            purpose: String(updated.purpose),
                            xmlContent: updated.content,
                            colors,
                            bpp,
                            resolution,
                        },
                    },
                },
                isDirty: true,
                dialogs: {
                    ...state.dialogs,
                    edit: false,
                },
            };
        }
        case 'CLEAN_SELECTED_LAYOUT': {
            return {
                ...state,
                multi: {
                    ...state.multi,
                    selectedLayouts: [],
                },
            };
        }
        case 'CLEAR_EDIT_DATA': {
            return {
                ...state,
                multi: {
                    ...state.multi,
                    editLayout: null,
                },
            };
        }
        case 'SET_SAVE_AS_NAME': {
            return {
                ...state,
                saveAsName: action.payload,
            };
        }
        case 'SET_PREVIEW_ERROR': {
            return {
                ...state,
                previewError: action.payload,
            };
        }
        case 'TOGGLE_DIALOG': {
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    [action.payload.key]: action.payload.open,
                },
            };
        }
        case 'TOGGLE_LOADING': {
            return {
                ...state,
                loadings: {
                    ...state.loadings,
                    [action.payload.key]: action.payload.loading,
                },
            };
        }
        case 'INIT_MULTI': {
            let displayType = '';
            let relationsInfo = {};
            const options = state.displayOptions;

            if (options.length > 0) {
                displayType = options[0]?.value ?? '';
            }

            if (action.payload) {
                const { layouts, relations } = action.payload;
                const layoutsById = buildLayoutsById(layouts);
                const formateRelations = mergeListAndConfig(relations, layoutsById);
                relationsInfo = {
                    initialRelations: formateRelations,
                    relations: formateRelations,
                    initialLayoutsById: layoutsById,
                    layoutsById,
                };

                const firstWithLayouts = state.displayOptions.find((opt) => {
                    if (formateRelations[opt.value]) {
                        return !isEmpty(formateRelations[opt.value]);
                    }
                    return false;
                }) as DisplayOption;

                displayType = firstWithLayouts?.value ?? displayType;
            }

            const { id, slug } = getStyleInfo(state.displayOptions, displayType);
            return {
                ...state,
                isDirty: false,
                multi: {
                    ...state.multi,
                    displayType,
                    styleId: id,
                    styleSlug: slug,
                    ...relationsInfo,
                },
            };
        }
        case 'SET_MULTI_RELATIONS': {
            const { displayType, styleSlug, selectedList } = action.payload;
            if (!styleSlug) return state;

            const { relations } = state.multi;
            const list = getStyleIds(relations, displayType, styleSlug);
            return {
                ...state,
                multi: {
                    ...state.multi,
                    relations: {
                        ...relations,
                        [displayType]: {
                            ...relations[displayType],
                            [styleSlug]: {
                                list: [...list, ...selectedList],
                                default_layout:
                                    relations?.[displayType]?.[styleSlug]?.default_layout ||
                                    list?.[0]?.uuid ||
                                    selectedList[0].uuid,
                            },
                        },
                    },
                },
            };
        }
        case 'REORDER_MULTI': {
            const { styleSlug, relations, displayType } = state.multi;
            if (!styleSlug) return state;
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    relations: setStyleIds(relations, displayType, styleSlug, action.newList),
                },
            };
        }
        case 'DETACH_MULTI': {
            const { styleSlug, relations, displayType } = state.multi;
            if (!styleSlug) return state;
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    relations: detachIds(relations, displayType, styleSlug, action.ids),
                },
            };
        }
        case 'SET_MULTI_DEFAULT': {
            const { relations, displayType, styleSlug } = state.multi;
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    relations: {
                        ...relations,
                        [displayType]: {
                            ...(relations[displayType] ?? {}),
                            [styleSlug as string]: {
                                ...(relations[displayType]?.[styleSlug as string] ?? {}),
                                default_layout: action.payload,
                            },
                        },
                    },
                },
            };
        }
        case 'SET_MULTI_DISPLAY': {
            const { id, slug } = getStyleInfo(state.displayOptions, action.payload);
            return {
                ...state,
                multi: {
                    ...state.multi,
                    displayType: action.payload,
                    styleId: id,
                    styleSlug: slug,
                },
            };
        }
        case 'ADD_NEW_SUB_LAYOUT': {
            const { layoutsById } = state.multi;
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    layoutsById: {
                        ...layoutsById,
                        ...action.payload,
                    },
                },
                dialogs: {
                    ...state.dialogs,
                    new: false,
                },
            };
        }
        case 'DELETE_SUB_LAYOUT': {
            const { layoutsById, relations } = state.multi;
            const { [action.payload]: _, ...rest } = layoutsById;
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    layoutsById: rest,
                    selectedLayouts: [],
                    relations: removeRelationsById(relations, action.payload),
                },
            };
        }
        case 'SET_STYLE_ID': {
            const styleId = action.styleId;
            const { slug } = getStyleInfo(state.displayOptions, state.multi.displayType, styleId);
            return {
                ...state,
                multi: {
                    ...state.multi,
                    styleId: styleId,
                    styleSlug: slug,
                },
            };
        }
        case 'SET_SELECTED_COLORS': {
            return {
                ...state,
                multi: {
                    ...state.multi,
                    selectedColors: action.payload,
                },
            };
        }
        case 'SET_SEARCH_SUB': {
            return {
                ...state,
                multi: {
                    ...state.multi,
                    searchSub: action.payload,
                },
            };
        }
        case 'DELETE_SUB_LAYOUT_PACKAGE': {
            return {
                ...state,
                isDirty: true,
                multi: {
                    ...state.multi,
                    selectedLayouts: [],
                    layoutsById: {},
                    relations: {},
                },
            };
        }
        case 'INIT_SINGLE': {
            let relationsInfo = {};
            let displayType = '';
            const options = state.displayOptions;
            if (options.length > 0) {
                displayType = options[0]?.value ?? '';
            }
            if (action.payload) {
                const { layouts, relations } = action.payload;
                const layoutsById = buildLayoutsById(layouts);
                const formateRelations = mergeListAndConfigForSingle(relations, layoutsById);
                relationsInfo = {
                    initialRelations: formateRelations,
                    relations: formateRelations,
                    initialLayoutsById: layoutsById,
                    layoutsById,
                };
                const firstWithLayouts = state.displayOptions.find((opt) => {
                    if (formateRelations[opt.value]) {
                        return formateRelations[opt.value].list.length > 0;
                    }
                    return false;
                }) as DisplayOption;
                displayType = firstWithLayouts?.value ?? displayType;
            }

            return {
                ...state,
                isDirty: false,
                single: {
                    ...state.single,
                    displayType,
                    ...relationsInfo,
                },
            };
        }
        case 'SET_SINGLE_DISPLAY': {
            return {
                ...state,
                single: {
                    ...state.single,
                    displayType: action.payload,
                },
            };
        }
        case 'REORDER_SINGLE': {
            const { relations, displayType } = state.single;
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    relations: setSingleStyleIds(relations, displayType, action.newList),
                },
            };
        }
        case 'DETACH_SINGLE': {
            const { relations, displayType } = state.single;
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    relations: detachSingleIds(relations, displayType, action.ids),
                },
            };
        }
        case 'SET_SINGLE_RELATIONS': {
            const { displayType, selectedList } = action.payload;
            const { relations } = state.single;
            const list = getSingleIds(relations, displayType);
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    relations: {
                        ...relations,
                        [displayType]: {
                            list: [...list, ...selectedList],
                            default_layout:
                                relations[displayType]?.default_layout || list?.[0]?.uuid || selectedList[0].uuid,
                        },
                    },
                },
            };
        }
        case 'CLEAN_SINGLE_SELECTED_LAYOUT': {
            return {
                ...state,
                single: {
                    ...state.single,
                    selectedLayouts: [],
                },
            };
        }
        case 'DELETE_SINGLE_LAYOUT_PACKAGE': {
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    selectedLayouts: [],
                    layoutsById: {},
                    relations: {},
                },
            };
        }
        case 'SET_SINGLE_SELECTED_SIZE': {
            return {
                ...state,
                single: {
                    ...state.single,
                    selectedSizes: action.payload,
                },
            };
        }
        case 'SET_SINGLE_SELECTED_COLORS': {
            return {
                ...state,
                single: {
                    ...state.single,
                    selectedColors: action.payload,
                },
            };
        }
        case 'SET_SINGLE_SEARCH_SUB': {
            return {
                ...state,
                single: {
                    ...state.single,
                    searchSub: action.payload,
                },
            };
        }
        case 'TOGGLE_SINGLE_SELECTED_LAYOUT': {
            const { id, checked } = action.payload;
            const { selectedLayouts } = state.single;
            return {
                ...state,
                single: {
                    ...state.single,
                    selectedLayouts: checked ? [...selectedLayouts, id] : selectedLayouts.filter((o) => o !== id),
                },
            };
        }
        case 'UPDATE_SINGLE_EDIT_DATA': {
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    edit: true,
                },
                single: {
                    ...state.single,
                    editLayout: action.payload,
                },
            };
        }
        case 'STORE_SINGLE_EDIT_DATA': {
            const { id, colors, updated, size, bpp } = action.payload;
            const { layoutsById } = state.single;
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    layoutsById: {
                        ...layoutsById,
                        [id]: {
                            ...layoutsById[id],
                            name: updated.name,
                            purpose: String(updated.purpose),
                            xmlContent: updated.content,
                            size,
                            colors,
                            bpp,
                        },
                    },
                },
                dialogs: {
                    ...state.dialogs,
                    edit: false,
                },
            };
        }
        case 'CLEAR_SINGLE_EDIT_DATA': {
            return {
                ...state,
                single: {
                    ...state.single,
                    editLayout: null,
                },
            };
        }
        case 'ADD_NEW_SINGLE_LAYOUT': {
            const { layoutsById } = state.single;
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    layoutsById: {
                        ...layoutsById,
                        ...action.payload,
                    },
                },
                dialogs: {
                    ...state.dialogs,
                    new: false,
                },
            };
        }
        case 'DELETE_SINGLE_LAYOUT': {
            const { layoutsById, relations } = state.single;
            const { [action.payload as string]: _, ...rest } = layoutsById;
            return {
                ...state,
                isDirty: true,
                single: {
                    ...state.single,
                    layoutsById: rest,
                    selectedLayouts: [],
                    relations: removeSingleRelationsById(relations, action.payload),
                },
            };
        }
        case 'SET_SINGLE_DEFAULT': {
            const { relations, displayType } = state.single;
            return {
                ...state,
                single: {
                    ...state.single,
                    relations: {
                        ...relations,
                        [displayType]: {
                            ...(relations[displayType] || {}),
                            default_layout: action.payload,
                        },
                    },
                },
            };
        }
        default:
            return state;
    }
};
export { defaultState };
